import axiosInstance from '@/plugins/axios';
import type { RubricaLoteFilters } from '@/services/ServiceRubricaLote';

export type StatusAnaliseRubrica = 'EM_ANALISE' | 'PENDENTE_DE_ESCLARECIMENTO' | 'ANALISADO';
export type StatusAnaliseEditavel = 'EM_ANALISE' | 'ANALISADO';

// Mesmo conjunto de filtros da análise em lote — o backend rubrica/tabela/consultar usa o
// mesmo RubricaFilters (grauRisco, natureza, tipo, situacao, ocorrencia, idTabela, excecao,
// usoEmFolha, cnpjEmpregador, statusAnalise, search).
export type RubricaTabelaFilters = RubricaLoteFilters;

export interface ClassificacaoTributaria {
  codigo: string;
  descricao: string;
}

export interface RubricaTabelaItem {
  [key: string]: unknown;
  idAnaliseRubrica: number;
  descricao: string | null;
  codigoRubrica: string | null;
  naturezaCodigo: number | null;
  naturezaNome: string | null;
  tipoNome: string | null;
  cnpjEmpregador: string | null;
  idTabela: string | null;
  inicioValidade: string | null;
  fimValidade: string | null;
  cp: string | null;
  irrf: string | null;
  fgts: string | null;
  cprp: string | null;
  pis: string | null;
  statusAnalise: string | null;
  etapaAnalise: string | null;
  situacao: string | null;
  grauRisco: string | null;
  diagnostico: string | null;
  classificacao: string | null;
  classificacaoTributaria: ClassificacaoTributaria | null;
  ocorrencia: { codigo: number; nome: string | null } | null;
  excecao: boolean | null;
  usoEmFolha: boolean;
  tipoCodigo: string | null;
  teto: boolean | null;
  dataProcessamento: string | null;
  dataRecepcao: string | null;
  ultimaUtilizacao: string | null;
  naturezaEsocialCodigo: number | null;
  possuiHistorico?: boolean;
}

export interface RubricaHistoricoAlteracao {
  id: number;
  changedField: string | null;
  changedFieldDescription: string | null;
  fromValue: string | null;
  fromValueDescription: string | null;
  toValue: string | null;
  toValueDescription: string | null;
}

/** Uma edição da análise, com os campos alterados nela. */
export interface RubricaHistoricoItem {
  id: number;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  changes: RubricaHistoricoAlteracao[];
}

export interface RubricaHistoricoPage {
  content: RubricaHistoricoItem[];
  page: number;
  size: number;
  /** Total de edições (não de campos). */
  totalElements: number;
  /** dh_recepcao do S-1010 (evt_tab_rubrica), como gravado. */
  dhRecepcao: string | null;
}

export interface RubricaTabelaRequest {
  filters: RubricaTabelaFilters;
  page: number;
  size: number;
  sort?: string;
}

export interface RubricaTabelaPage {
  content: RubricaTabelaItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface RubricaOcorrenciaOption {
  codigo: number;
  nome: string;
  grauRisco: string | null;
  diagnostico: string | null;
  classificacao: string | null;
}

export interface RubricaEditRequest {
  tipo: string | null;
  natureza: number | null;
  ocorrencia?: number;
  statusAnalise?: StatusAnaliseEditavel;
  cp: string | null;
  irrf: string | null;
  fgts: string | null;
  cprp: string | null;
  pis: string | null;
  teto: boolean | null;
}

export interface RubricaContagem {
  emAnalise: number;
  analisado: number;
}

export default class ServiceRubricaTabela {
  static consultar(body: RubricaTabelaRequest) {
    return axiosInstance.post<RubricaTabelaPage>('rubrica/tabela/consultar', body, {
      signal: new AbortController().signal,
    });
  }

  static obter(idAnaliseRubrica: number) {
    return axiosInstance.get<RubricaTabelaItem>(`rubrica/tabela/${idAnaliseRubrica}`, {
      signal: new AbortController().signal,
      headers: { 'Cache-Control': 'no-cache' },
    });
  }

  static historico(idAnaliseRubrica: number, page = 0, size = 50) {
    return axiosInstance.post<RubricaHistoricoPage>(
      `rubrica/tabela/${idAnaliseRubrica}/historico`,
      {},
      { params: { page, size }, signal: new AbortController().signal },
    );
  }

  static consultarOcorrencias(signal = new AbortController().signal) {
    return axiosInstance.get<RubricaOcorrenciaOption[]>('rubrica/tabela/ocorrencias', {
      signal,
    });
  }

  static editar(idAnaliseRubrica: number, body: RubricaEditRequest) {
    return axiosInstance.put<RubricaTabelaItem>(`rubrica/tabela/${idAnaliseRubrica}`, body, {
      signal: new AbortController().signal,
    });
  }

  static contar(filters: RubricaTabelaFilters) {
    return axiosInstance.post<RubricaContagem>(
      'rubrica/tabela/contagem',
      { filters },
      { signal: new AbortController().signal },
    );
  }

  // CON-5375: enfileira o export da tabela (respeita os filtros atuais) — o arquivo
  // aparece em Arquivos > Exportações, como os demais exports.
  static exportar(filters: RubricaTabelaFilters) {
    return axiosInstance.post(
      'rubrica/tabela/export',
      { filters },
      { signal: new AbortController().signal },
    );
  }
}
